import _ from "lodash";
import React from "react";

import { graphql, compose } from "lib/apolloClient";
import withLoader from "UIComponents/GqlWithLoader";
import { getDepartmentQuery } from "MessagingV2/modules/MessagingQuery";
import { getDepartmentId, CHAT_TYPE_ENUM } from "MessagingV2/modules/Utils";
import { handleCreateDepartmentChannel } from "MessagingV2/modules/MessagingModule";
import MessagingAvatar from "MessagingV2/components/ThreadList/Avatar";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import classNames from "classnames";
import useI18n from "Hooks/UseI18n";
import { useDispatch } from "Hooks/useDispatch";
import { Button, EmptyState, Tooltip } from "@toddle-edu/ds-web";
import { ChatIllustration } from "@toddle-edu/ds-theme";

import MembersModal from "./MembersModal";
import classes from "./DepartmentDetailContainer.scss";

const LABEL_LENGTH = 70;

const DepartmentDetailContainer = ({ department }) => {
  const { t } = useI18n(["common"]);
  const dispatch = useDispatch();
  const [showMemberModal, setShowMemberModal] = React.useState(false);

  const { id, name, profileImage } = department;
  const totalMembers = _.get(department, "users.totalCount", 0);

  const handleDepartMentChannelCreate = () => {
    dispatch(handleCreateDepartmentChannel({ id, isWidget: false }));
  };

  const handleViewMemberClick = () => {
    setShowMemberModal(true);
  };

  const onCloseMemberModal = () => {
    setShowMemberModal(false);
  };

  return (
    <>
      <div className={classes.root}>
        <div className={classes.headerContainer}>
          <MessagingAvatar
            id={id}
            name={name}
            chatType={CHAT_TYPE_ENUM.CHANNEL_DEPARTMENT}
            url={profileImage}
          />

          <div className={classes.departmentTitleAndSubtitleContainer}>
            <ConditionalWrapper
              wrapper={<Tooltip tooltip={name} />}
              condition={_.size(name) > LABEL_LENGTH}
              type={"wrapperAsElement"}
            >
              <div className="text-body">
                {_.truncate(name, { length: LABEL_LENGTH })}
              </div>
            </ConditionalWrapper>
            <div className={classNames("text-body-s", classes.subtitle)}>
              {t("common:count_members", {
                count: totalMembers,
              })}
            </div>
          </div>
        </div>
        <div className={classes.bodyContainer}>
          <div className={classes.emptyStateContainer}>
            <EmptyState
              dsVersion="2.0"
              illustration={ChatIllustration}
              title={t("common:start_conversation_with_label", {
                label: name,
              })}
              subtitle={t("common:department_detail_subtext")}
            />
          </div>
        </div>
        <div className={classes.footerContainer}>
          <Button
            dsVersion="2.0"
            type="outlined"
            variant="neutral"
            onClick={handleViewMemberClick}
          >
            {t("common:view_members")}
          </Button>
          <Button
            dsVersion="2.0"
            type="fill"
            variant="primary"
            onClick={handleDepartMentChannelCreate}
          >
            {t("common:create_channel")}
          </Button>
        </div>
      </div>
      {showMemberModal ? (
        <MembersModal department={department} onClose={onCloseMemberModal} />
      ) : null}
    </>
  );
};

export default compose(
  graphql(getDepartmentQuery, {
    name: "getDepartment",
    options: ({ chatId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: getDepartmentId(chatId),
        portalType: "PLANNER",
      },
    }),
    props({ getDepartment }) {
      const node = _.get(getDepartment, "node", {});
      return {
        isLoading: _.includes([1, 2], getDepartment["networkStatus"]),
        isData: !_.isEmpty(node),
        department: node,
      };
    },
  }),
  withLoader
)(DepartmentDetailContainer);
